import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Pair up every `**` with its closer and trim whitespace inside, because CommonMark
 * rejects bold runs whose opener is followed by whitespace or whose closer is
 * preceded by whitespace. Without this, LLM output like `**Devis — **` leaks raw
 * asterisks to the user.
 *
 * Implemented as a state machine rather than a regex so cross-line content and
 * adjacent runs are handled the same way.
 */
function normalizeBoldDelimiters(content: string): string {
    let out = '';
    let i = 0;
    while (i < content.length) {
        if (content[i] === '*' && content[i + 1] === '*') {
            const start = i + 2;
            const end = content.indexOf('**', start);
            if (end === -1) {
                out += content.slice(i);
                break;
            }
            const inner = content.slice(start, end);
            const trimmed = inner.replace(/^\s+|\s+$/g, '');
            const leading = inner.length !== inner.replace(/^\s+/, '').length ? ' ' : '';
            const trailing = inner.length !== inner.replace(/\s+$/, '').length ? ' ' : '';
            if (trimmed === '') {
                // Nothing to bold — drop the empty `**...**` rather than leak asterisks.
                out += leading || trailing ? ' ' : '';
            } else {
                out += `${leading}**${trimmed}**${trailing}`;
            }
            i = end + 2;
            continue;
        }
        out += content[i];
        i++;
    }
    return out;
}

export function renderMarkdown(content: string): string {
    const html = marked.parse(normalizeBoldDelimiters(content), { breaks: true }) as string;
    return DOMPurify.sanitize(html);
}
